"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";

import { objectToFormData } from "@/libs/shared/api/objectToFormData";
import type {
  CreatePlaylistModel,
  CreateVideoProps,
  EditVideoProps,
  PlaylistVodsSortMode,
  VideoModelProps,
} from "./vod.types";

const tagTypes = [
  "Vod" as const,
  "Vods" as const,
  "Playlist" as const,
  "Playlists" as const,
  "PlaylistAssignments" as const,
  "Subscription" as const,
];

export const invalidateVods = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(vodsApi.util.invalidateTags([tag as any]));
      });
    });
  };
};

export const vodsApi = createApi({
  baseQuery,
  reducerPath: "vodsApi",
  tagTypes,
  endpoints: (builder) => ({
    getVideos: builder.query({
      query: ({
        perPage,
        page,
        orderBy,
        orderByDirection,
        name,
      }: {
        perPage: number;
        page: string;
        orderBy: string;
        orderByDirection: string;
        name: string;
      }) => ({
        url: `pt/vods?per_page=${perPage}&page=${page}&order_by=${orderBy}&order_by_direction=${orderByDirection}&name=${name}`,
      }),
      providesTags: ["Vods"],
    }),
    getVideo: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `pt/vods/${id}`,
      }),
      providesTags: ["Vod"],
    }),
    getViews: builder.query({
      query: () => ({
        url: `/pt/views`,
      }),
    }),
    deleteVideo: builder.mutation({
      query: (id: string) => {
        return {
          url: `pt/vods/${id}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateVods(["Vods"]),
    }),
    createVideo: builder.mutation({
      query: (video: CreateVideoProps) => {
        const formData = objectToFormData(video);
        return {
          url: "pt/vods",
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Vods", "Vod"]),
    }),
    editVideo: builder.mutation<
      EditVideoProps,
      { id: string; video: EditVideoProps }
    >({
      query: ({ id, video }: { id: string; video: EditVideoProps }) => {
        const formData = objectToFormData(video);
        return {
          url: `pt/vods/${id}`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Vods", "Vod"]),
    }),
    getPlaylists: builder.query({
      query: ({
        perPage,
        page,
        orderBy,
        name,
      }: {
        perPage: number;
        page: string;
        orderBy: string;
        name: string;
      }) => ({
        url: `pt/playlists?per_page=${perPage}&page=${page}&order_by=${orderBy}&name=${name}`,
      }),
      providesTags: ["Playlists"],
    }),
    getPlaylist: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `pt/playlists/${id}`,
      }),
      providesTags: ["Playlist"],
    }),
    getPlaylistAssignments: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `pt/playlists/${id}/assignments`,
      }),
      providesTags: ["PlaylistAssignments"],
    }),
    createPlaylist: builder.mutation({
      query: (playlist: CreatePlaylistModel) => {
        const formData = objectToFormData(playlist);
        return {
          url: `pt/playlists`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Playlists"]),
    }),
    editPlaylist: builder.mutation({
      query: ({
        id,
        playlist,
      }: {
        id: string;
        playlist: CreatePlaylistModel;
      }) => {
        const formData = objectToFormData(playlist);
        return {
          url: `pt/playlists/${id}`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Playlists", "Playlist"]),
    }),
    deletePlaylist: builder.mutation({
      query: (id: string) => {
        return {
          url: `pt/playlists/${id}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateVods(["Playlists"]),
    }),
    updatePlaylistSortMode: builder.mutation<
      unknown,
      { id: string; vodsSortMode: PlaylistVodsSortMode }
    >({
      query: ({ id, vodsSortMode }) => {
        const formData = new FormData();
        formData.append("vods_sort_mode", vodsSortMode);
        return {
          url: `pt/playlists/${id}`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Playlists", "Playlist"]),
    }),
    reorderPlaylistVods: builder.mutation<
      unknown,
      { id: string; vodIds: number[] }
    >({
      query: ({ id, vodIds }) => ({
        url: `pt/playlists/${id}/vods/reorder`,
        method: "PUT",
        body: { vod_ids: vodIds },
      }),
      async onQueryStarted(
        { id, vodIds },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          vodsApi.util.updateQueryData(
            "getPlaylist",
            { id },
            (draft) => {
              if (!draft?.vods || draft.vods.length === 0) {
                return;
              }
              if (draft.vods.length !== vodIds.length) {
                return;
              }
              const byId = new Map<number, VideoModelProps>();
              for (const vod of draft.vods) {
                byId.set(vod.id, vod as VideoModelProps);
              }
              const reordered: VideoModelProps[] = [];
              for (const vid of vodIds) {
                const v = byId.get(vid);
                if (v !== undefined) {
                  reordered.push(v);
                }
              }
              if (reordered.length !== draft.vods.length) {
                return;
              }
              draft.vods = reordered;
              const idNum = Number(id);
              reordered.forEach((v, index) => {
                const rank = index + 1;
                v.pivot = {
                  ...v.pivot,
                  rank,
                  playlist_id: v.pivot?.playlist_id ?? idNum,
                  vod_id: v.pivot?.vod_id ?? v.id,
                };
              });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          if ("undo" in patchResult && typeof patchResult.undo === "function") {
            patchResult.undo();
          }
        }
      },
    }),
    getPlaylistVideo: builder.mutation({
      query: () => ({
        url: `pt/vods?light=1&fields=id,name,views,thumbnail`,
      }),
    }),
    addPlaylistVideo: builder.mutation({
      query: ({ id, vods }: { id: string; vods: string }) => {
        const formData = objectToFormData({ vods });
        return {
          url: `pt/playlists/${id}/vods`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Playlists", "Playlist"]),
    }),
    deletePlaylistVideo: builder.mutation({
      query: ({ id, vod }: { id: string; vod: string }) => {
        return {
          url: `pt/playlists/${id}/vod/${vod}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateVods(["Playlists"]),
    }),
    assignVideo: builder.mutation({
      query: ({
        id,
        assignModel,
      }: {
        id: string;
        assignModel: {
          groups?: string;
          customers?: string;
          weeks?: number;
          start?: string;
        };
      }) => {
        const formData = objectToFormData(assignModel);
        return {
          url: `/pt/vods/${id}/assignments`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["Vods", "Vod"]),
    }),
    assignPlaylist: builder.mutation({
      query: ({
        id,
        assignModel,
      }: {
        id: string;
        assignModel: {
          groups?: string;
          customers?: string;
          weeks?: number;
          start?: string;
        };
      }) => {
        const formData = objectToFormData(assignModel);
        return {
          url: `/pt/playlists/${id}/assignments`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateVods(["PlaylistAssignments"]),
    }),
    getGroups: builder.query({
      query: ({ perPage, page }: { perPage: number; page: string }) => ({
        url: `pt/groups?per_page=${perPage}&page=${page}`,
      }),
    }),
    getSubscription: builder.query({
      query: () => ({
        url: `pt/account/subscription`,
      }),
      providesTags: ["Subscription"],
    }),
  }),
});

export const {
  useGetVideosQuery,
  useGetVideoQuery,
  useGetViewsQuery,
  useCreateVideoMutation,
  useEditVideoMutation,
  useDeleteVideoMutation,
  useGetPlaylistsQuery,
  useGetPlaylistQuery,
  useCreatePlaylistMutation,
  useEditPlaylistMutation,
  useDeletePlaylistMutation,
  useUpdatePlaylistSortModeMutation,
  useReorderPlaylistVodsMutation,
  useGetPlaylistVideoMutation,
  useAddPlaylistVideoMutation,
  useDeletePlaylistVideoMutation,
  useAssignVideoMutation,
  useAssignPlaylistMutation,
  useGetPlaylistAssignmentsQuery,
  useGetGroupsQuery,
} = vodsApi;
